/**
 * The house badge: a word on a tinted ground, never colour alone. Each variant maps a wire value to its words and a
 * tone; the tones are token blends (`.badge-<tone>` in index.css, the same classes every other badge in the app
 * wears) with dark text on a light tint (light theme) and light text on a dark tint (dark theme), checked for AA by
 * `scripts/check-contrast.mjs`.
 */

import type { ReactNode } from 'react';

export type BadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral';

export type BadgeVariant = 'status' | 'kind' | 'maturity' | 'audience' | 'provenance' | 'freshness';

interface BadgeStyle {
  label: string;
  tone: BadgeTone;
}

const STYLES: Record<BadgeVariant, Record<string, BadgeStyle>> = {
  status: {
    draft: { label: 'Draft', tone: 'warning' },
    published: { label: 'Published', tone: 'success' },
    archived: { label: 'Archived', tone: 'neutral' },
  },
  kind: {
    platform: { label: 'Platform', tone: 'info' },
    accelerator: { label: 'Accelerator', tone: 'info' },
    framework: { label: 'Framework', tone: 'info' },
    offer: { label: 'Offer', tone: 'info' },
  },
  maturity: {
    concept: { label: 'Concept', tone: 'neutral' },
    pilot: { label: 'Pilot', tone: 'info' },
    available: { label: 'Available', tone: 'success' },
  },
  audience: {
    internal: { label: 'Internal', tone: 'warning' },
    shareable: { label: 'Shareable', tone: 'success' },
  },
  provenance: {
    client_measured: { label: 'Client measured', tone: 'success' },
    self_measured: { label: 'Benchmark', tone: 'info' },
    expected: { label: 'Expected', tone: 'neutral' },
  },
  freshness: {
    fresh: { label: 'Fresh', tone: 'success' },
    stale: { label: 'Stale', tone: 'warning' },
    unconfirmed: { label: 'Not confirmed', tone: 'neutral' },
  },
};

/** The words and tone of a value; an unknown value reads as itself, in the neutral tone. */
export function badgeStyle(variant: BadgeVariant, value: string): BadgeStyle {
  return STYLES[variant][value] ?? { label: value, tone: 'neutral' };
}

/** The words a variant uses for a value (for filters and table text). */
export function badgeLabel(variant: BadgeVariant, value: string): string {
  return badgeStyle(variant, value).label;
}

type BadgeProps =
  | { variant: BadgeVariant; value: string; tone?: never; children?: never; title?: string; icon?: ReactNode }
  | { tone: BadgeTone; children: ReactNode; variant?: never; value?: never; title?: string; icon?: ReactNode };

export function Badge(props: BadgeProps) {
  const { tone, label } = props.variant
    ? badgeStyle(props.variant, props.value)
    : { tone: props.tone, label: props.children };
  return (
    <span className={`badge badge-${tone}`} title={props.title}>
      {props.icon}
      {label}
    </span>
  );
}
